import * as types from './mutations-types';
import { API, graphqlOperation } from 'aws-amplify';
import {
  createProject,
  createUserProject,
  deleteProject,
  deleteUserProject,
  updateProject,
} from '@/graphql/mutations';
import { listUserProjects } from '@/graphql/queries';
import { PROJECTS_LIMIT } from '../../../constants/constants';
import { getCurUser } from '../../../constants/fromLocalStorageConstants';
import { logError } from '@/utils/loggerUtils';

export const actions = {
  async FETCH_PROJECTS({ dispatch, commit }) {
    try {
      // @todo make a wrapper for local storage to interact with instead of using directly
      const user = JSON.parse(localStorage.getItem('user'));

      const userProjects = await API.graphql(
        graphqlOperation(listUserProjects, {
          filter: {
            userID: {
              eq: user.email,
            },
          },
          PROJECTS_LIMIT,
        })
      );

      const projects = userProjects.data.listUserProjects.items;
      await commit(types.FETCH_PROJECTS_SUCCESS, { projects: projects });

      // set first project to be default (after sorting in mutations)
      if (projects.length === 0) {
        //create a new project as a default project for new user
        const projectPreference = {
          searchMode: 'All Content',
          products: [],
        };
        const project = {
          name: 'Default Project',
          description: '',
          projectPreference: JSON.stringify(projectPreference),
        };
        await dispatch('STORE_PROJECT', { project, users: [] });
      }
    } catch (e) {
      logError(e);
    }
  },

  async STORE_PROJECT({ commit }, { project, users }) {
    const curUserID = getCurUser().email;
    const accessGroups = getCurUser().groups;
    try {
      const response = await API.graphql(
        graphqlOperation(createProject, {
          input: project,
          owner: curUserID,
        })
      );

      const projectID = response.data.createProject.id;

      // add the owner first since if this fails we don't want to add anyone else
      // @todo we need batch request ability from the backend to make our changes atomic
      const userProjectResponse = await API.graphql(
        graphqlOperation(createUserProject, {
          input: {
            projectID,
            userID: curUserID,
            accessGroups,
          },
        })
      );

      const requests = users.map((userID) =>
        API.graphql(
          graphqlOperation(createUserProject, {
            input: {
              projectID,
              userID,
              accessGroups,
            },
          })
        )
      );
      await Promise.all(requests);

      commit(
        types.STORE_PROJECT_SUCCESS,
        userProjectResponse.data.createUserProject
      );
      const result = {
        status: types.STORE_PROJECT_SUCCESS,
        userProject: userProjectResponse.data.createUserProject,
      };
      return result;
    } catch (e) {
      logError(e);
      return types.STORE_PROJECT_FAILURE;
    }
  },

  async INVITE_USERS(_, { projectID, users }) {
    try {
      const {
        data: { listUserProjects: items },
      } = await API.graphql(
        graphqlOperation(listUserProjects, {
          filter: {
            projectID: {
              eq: projectID,
            },
          },
        })
      );
      const existingUsers = items.items.map(({ userID }) => userID);
      const newUsers = users.filter((user) => !existingUsers.includes(user));

      const requests = newUsers.map((user) =>
        API.graphql(
          graphqlOperation(createUserProject, {
            input: {
              projectID,
              userID: user,
              accessGroups: getCurUser().groups,
            },
          })
        )
      );
      await Promise.all(requests);
    } catch (e) {
      logError(e);
    }
  },

  async DELETE_USERS(_, { projectID, users }) {
    try {
      const {
        data: { listUserProjects: items },
      } = await API.graphql(
        graphqlOperation(listUserProjects, {
          filter: {
            projectID: {
              eq: projectID,
            },
          },
        })
      );

      const deleteUserProjectIDs = items.items.reduce(
        (acc, { id, userID }) => (users.includes(userID) ? [...acc, id] : acc),
        []
      );

      const requests = deleteUserProjectIDs.map((id) =>
        API.graphql(
          graphqlOperation(deleteUserProject, {
            input: {
              id,
            },
          })
        )
      );

      await Promise.all(requests);
    } catch (e) {
      logError(e);
    }
  },

  async UPDATE_PROJECT({ dispatch, commit }, payload) {
    if (payload.projectPreference) {
      try {
        const project = await API.graphql(
          graphqlOperation(updateProject, {
            input: {
              id: payload.id,
              name: payload.name,
              projectPreference: payload.projectPreference,
            },
          })
        );
        commit(types.UPDATE_PROJECT_SUCCESS, project.data.updateProject);
      } catch (e) {
        logError(e);
      }
    } else {
      try {
        const {
          projectID,
          description,
          projectName,
          inviteUsers,
          deleteUsers,
        } = payload;
        const project = await API.graphql(
          graphqlOperation(updateProject, {
            input: {
              id: projectID,
              name: projectName,
              description: description,
            },
          })
        );

        if (inviteUsers.length > 0) {
          await dispatch('INVITE_USERS', {
            projectID: projectID,
            users: inviteUsers,
          });
        }

        if (deleteUsers.length > 0) {
          await dispatch('DELETE_USERS', {
            projectID: projectID,
            users: deleteUsers,
          });
        }

        commit(types.UPDATE_PROJECT_SUCCESS, project.data.updateProject);
      } catch (e) {
        logError(e);
      }
    }
  },

  async DESTROY_USER_PROJECT({ commit }, projectID) {
    try {
      const userProjects = await API.graphql(
        graphqlOperation(listUserProjects, {
          filter: {
            projectID: {
              eq: projectID,
            },
          },
        })
      );

      const requests = userProjects.data.listUserProjects.items.map(({ id }) =>
        API.graphql(
          graphqlOperation(deleteUserProject, {
            input: {
              id,
            },
          })
        )
      );
      await Promise.all(requests);
      commit(types.DESTROY_PROJECT_SUCCESS);
    } catch (e) {
      logError(e);
    }
  },

  async DESTROY_PROJECT({ commit }, project_id) {
    try {
      await API.graphql(
        graphqlOperation(deleteProject, {
          input: {
            id: project_id,
          },
        })
      );
      commit(types.DESTROY_PROJECT_SUCCESS);
      return types.DESTROY_PROJECT_SUCCESS;
    } catch (e) {
      logError(e);
      return types.DESTROY_PROJECT_FAILURE;
    }
  },
};
